if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      // The counter fix runs for any main product gallery (data-main-product is only
      // emitted by sections/main-product.liquid), with or without thumbnails.
      // featured-product and the quick view keep the native slider behaviour.
      if (this.hasAttribute('data-main-product')) this.initCounterSync();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();

      // On desktop the thumbnail_slider layout shows one image at a time, so the
      // scroll-based slider cannot drive the arrows or the counter. Below 750px
      // the native SliderComponent keeps that job. Thumbnail and desktop features
      // stay gated on the thumbnail_slider layout.
      if (!this.closest('.product--thumbnail_slider')) return;
      if (this.dataset.desktopLayout === 'thumbnail_slider') this.initDesktopControls();
      this.initThumbnailFade();
    }

    // The native SliderComponent counts pages as slides - floor(width / offset) + 1,
    // and the offset includes the gap, so a one-slide-per-view gallery reports n + 1
    // pages. Whenever the desktop thumbnail controls are not driving the counter
    // (mobile, or desktop with thumbnails hidden) this keeps it honest:
    // total = visible slides (variant filtered slides are hidden),
    // current = slide nearest to the scroll position.
    initCounterSync() {
      const viewer = this.elements.viewer;
      const slider = viewer && viewer.querySelector('.slider');
      const current = viewer && viewer.querySelector('.slider-counter--current');
      const total = viewer && viewer.querySelector('.slider-counter--total');
      if (!slider || !current || !total) return;

      const sync = () => {
        if (this.usesDesktopControls()) return;
        const slides = Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter((item) => item.clientWidth > 0);
        if (!slides.length) return;
        const offset = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : slider.clientWidth;
        const index = Math.min(Math.max(Math.round(Math.abs(slider.scrollLeft) / offset), 0), slides.length - 1);
        if (current.textContent !== String(index + 1)) current.textContent = index + 1;
        if (total.textContent !== String(slides.length)) total.textContent = slides.length;
      };
      // Runs after the native handler, which writes the same nodes on every scroll.
      const syncLater = () => window.requestAnimationFrame(sync);

      slider.addEventListener('scroll', syncLater, { passive: true });
      new ResizeObserver(syncLater).observe(slider);
      const observer = new MutationObserver(syncLater);
      [current, total].forEach((node) => observer.observe(node, { childList: true, characterData: true, subtree: true }));
      this.mql.addEventListener('change', syncLater);
      syncLater();
    }

    // Vertical thumbnail column: flags whether more thumbnails are hidden above
    // or below (data-fade-top / data-fade-bottom). The CSS turns the flags into a
    // mask fade, only when the editor setting adds .product--thumbs-fade.
    initThumbnailFade() {
      const list = this.elements.thumbnails.slider;
      if (!list || !this.closest('.product--thumbs-fade')) return;

      const update = () => {
        const vertical = this.mql.matches && list.offsetParent !== null && list.scrollHeight > list.clientHeight + 1
          && window.getComputedStyle(list).flexDirection === 'column';
        list.toggleAttribute('data-fade-top', vertical && list.scrollTop > 1);
        list.toggleAttribute('data-fade-bottom', vertical && list.scrollTop + list.clientHeight < list.scrollHeight - 1);
      };

      list.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(list);
      new MutationObserver(update).observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
      this.mql.addEventListener('change', update);
      window.addEventListener('load', update);
      update();
    }

    initDesktopControls() {
      const viewer = this.elements.viewer;
      this.desktopControls = {
        prev: viewer.querySelector('button[name="previous"]'),
        next: viewer.querySelector('button[name="next"]'),
        current: viewer.querySelector('.slider-counter--current'),
        total: viewer.querySelector('.slider-counter--total')
      };
      // Capture phase on the viewer: runs before the native SliderComponent button
      // handler, which would otherwise also scroll the list on desktop.
      viewer.addEventListener('click', (event) => {
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button || !viewer.contains(button) || !this.usesDesktopControls()) return;
        event.stopImmediatePropagation();
        this.stepMedia(button.name === 'next' ? 1 : -1, event);
      }, true);
      this.mql.addEventListener('change', this.updateDesktopControls.bind(this));
      this.updateDesktopControls();
    }

    usesDesktopControls() {
      return this.mql.matches && this.desktopControls && this.elements.thumbnails.offsetParent !== null;
    }

    visibleThumbnails() {
      return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter((item) => item.offsetParent !== null);
    }

    stepMedia(direction, event) {
      if (!this.usesDesktopControls()) return;
      if (event) event.preventDefault();
      const items = this.visibleThumbnails();
      const index = items.findIndex((item) => item.classList.contains('is-active'));
      const target = items[index + direction];
      if (target) this.setActiveMedia(target.dataset.target, false, false);
    }

    updateDesktopControls() {
      if (!this.usesDesktopControls()) return;
      const items = this.visibleThumbnails();
      const index = Math.max(items.findIndex((item) => item.classList.contains('is-active')), 0);
      const { prev, next, current, total } = this.desktopControls;

      if (current && total) {
        current.textContent = index + 1;
        total.textContent = items.length;
      }
      if (prev) prev.toggleAttribute('disabled', index === 0);
      if (next) next.toggleAttribute('disabled', index >= items.length - 1);

      // Keep the active thumbnail in view when the thumbnails are a vertical column.
      const list = this.elements.thumbnails.slider;
      const active = items[index];
      if (list && active && list.scrollHeight > list.clientHeight) {
        if (active.offsetTop < list.scrollTop) {
          list.scrollTop = active.offsetTop;
        } else if (active.offsetTop + active.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTop = active.offsetTop + active.offsetHeight - list.clientHeight;
        }
      }
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      this.updateDesktopControls?.();

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
